import NavBar from '../components/NavBar';
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { useAuth } from '../AuthContext';

export default function ApiKeys() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [keys, setKeys] = useState([]);
  const [modal, setModal] = useState(false);
  const [newKey, setNewKey] = useState(null); // revealed once after creation
  const [form, setForm] = useState({ name: '', scopes: ['read'], rate_limit_per_min: 60, expires_at: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get('/api/keys'); setKeys(data); } catch {}
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!form.name) { setError('Name required'); return; }
    setError(''); setLoading(true);
    try {
      const { data } = await api.post('/api/keys', form);
      setNewKey(data);
      setModal(false);
      setForm({ name: '', scopes: ['read'], rate_limit_per_min: 60, expires_at: '' });
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create key');
    } finally { setLoading(false); }
  };

  const toggleScope = (scope) => {
    setForm(f => ({
      ...f,
      scopes: f.scopes.includes(scope) ? f.scopes.filter(s=>s!==scope) : [...f.scopes, scope]
    }));
  };

  const toggleEnabled = async (key) => {
    await api.patch(`/api/keys/${key.id}`, { enabled: !key.enabled });
    load();
  };

  const deleteKey = async (key) => {
    if (!window.confirm(`Delete API key "${key.name}"?`)) return;
    await api.delete(`/api/keys/${key.id}`);
    load();
  };

  const copyKey = () => {
    navigator.clipboard.writeText(newKey.key);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const scopeColors = { read: '#00e676', stream: '#f59e0b', admin: '#ff4d4d' };

  const s = {
    page: { minHeight:'100vh', background:'var(--bg)' },
    nav: { display:'flex', alignItems:'center', justifyContent:'space-between', padding:'0 32px', height:'58px', borderBottom:'1px solid var(--border)', background:'var(--surface)', position:'sticky', top:0, zIndex:10 },
    navLogo: { display:'flex', alignItems:'center', gap:'9px', fontWeight:'800', fontSize:'17px' },
    navDot: { width:'9px', height:'9px', borderRadius:'50%', background:'var(--signal)', boxShadow:'0 0 8px var(--signal)' },
    navRight: { display:'flex', alignItems:'center', gap:'10px' },
    navBtn: (h) => ({ fontSize:'13px', padding:'6px 14px', border:`1px solid ${h?'rgba(0,230,118,0.3)':'var(--border)'}`, borderRadius:'7px', background:'transparent', color:h?'var(--signal)':'var(--text-muted)', cursor:'pointer' }),
    main: { maxWidth:'900px', margin:'0 auto', padding:'36px 24px' },
    header: { display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'28px' },
    title: { fontSize:'24px', fontWeight:'800', letterSpacing:'-0.5px' },
    addBtn: { background:'var(--signal)', color:'#000', fontWeight:'700', fontSize:'13px', borderRadius:'8px', padding:'9px 18px', cursor:'pointer', border:'none' },
    // Revealed key banner
    keyBanner: { background:'rgba(0,230,118,0.06)', border:'1px solid rgba(0,230,118,0.25)', borderRadius:'var(--radius-lg)', padding:'20px 24px', marginBottom:'24px' },
    keyBannerTitle: { fontWeight:'700', fontSize:'15px', marginBottom:'6px', color:'var(--signal)' },
    keyBannerSub: { fontSize:'13px', color:'var(--text-muted)', marginBottom:'14px' },
    keyDisplay: { fontFamily:'var(--mono)', fontSize:'13px', background:'var(--surface2)', padding:'12px 16px', borderRadius:'8px', border:'1px solid var(--border)', wordBreak:'break-all', color:'var(--text)', marginBottom:'10px' },
    copyBtn: (copied) => ({ padding:'7px 16px', background:copied?'rgba(0,230,118,0.15)':'var(--signal)', color:copied?'var(--signal)':'#000', fontWeight:'700', border:copied?'1px solid rgba(0,230,118,0.3)':'none', borderRadius:'7px', cursor:'pointer', fontSize:'13px' }),
    // Key list
    keyCard: (enabled) => ({ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'10px', padding:'14px 18px', display:'flex', alignItems:'center', gap:'12px', marginBottom:'8px', opacity:enabled?1:0.6 }),
    keyIcon: { width:'36px', height:'36px', borderRadius:'8px', background:'rgba(0,230,118,0.08)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'16px', flexShrink:0 },
    keyInfo: { flex:1 },
    keyName: { fontWeight:'600', fontSize:'14px', marginBottom:'3px' },
    keyMeta: { fontSize:'12px', color:'var(--text-muted)', display:'flex', gap:'10px', flexWrap:'wrap' },
    scopeBadge: (scope) => ({ fontSize:'10px', fontWeight:'700', padding:'2px 6px', borderRadius:'4px', background:`${scopeColors[scope]}15`, color:scopeColors[scope], border:`1px solid ${scopeColors[scope]}30` }),
    actions: { display:'flex', gap:'6px' },
    toggleBtn: (on) => ({ fontSize:'12px', fontWeight:'700', padding:'5px 12px', borderRadius:'7px', background:on?'rgba(0,230,118,0.08)':'var(--surface2)', color:on?'var(--signal)':'var(--text-muted)', border:`1px solid ${on?'rgba(0,230,118,0.2)':'var(--border)'}`, cursor:'pointer' }),
    delBtn: { background:'var(--danger-bg)', border:'1px solid rgba(255,77,77,0.2)', color:'var(--danger)', borderRadius:'7px', padding:'5px 12px', fontSize:'12px', cursor:'pointer' },
    mono: { fontFamily:'var(--mono)', fontSize:'11px', color:'var(--text-muted)' },
    // Docs card
    docsCard: { background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'var(--radius-lg)', padding:'20px 24px', marginTop:'24px' },
    docsTitle: { fontWeight:'700', fontSize:'14px', marginBottom:'14px' },
    endpoint: { fontFamily:'var(--mono)', fontSize:'12px', padding:'8px 12px', background:'var(--surface2)', borderRadius:'6px', marginBottom:'6px', display:'flex', gap:'12px', alignItems:'center' },
    method: (m) => ({ fontSize:'10px', fontWeight:'800', padding:'2px 6px', borderRadius:'4px', background:m==='GET'?'rgba(0,230,118,0.1)':'rgba(245,158,11,0.1)', color:m==='GET'?'#00e676':'#f59e0b', border:`1px solid ${m==='GET'?'rgba(0,230,118,0.2)':'rgba(245,158,11,0.2)'}`, flexShrink:0 }),
    // Modal
    overlay: { position:'fixed', inset:0, background:'rgba(0,0,0,0.75)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:100, padding:'24px' },
    modalBox: { background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'var(--radius-lg)', padding:'32px', width:'100%', maxWidth:'460px' },
    modalTitle: { fontSize:'20px', fontWeight:'800', marginBottom:'24px' },
    field: { marginBottom:'16px' },
    label: { display:'block', fontSize:'11px', fontWeight:'700', color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.8px', marginBottom:'7px' },
    scopeGrid: { display:'flex', gap:'8px' },
    scopeBtn: (active, scope) => ({ padding:'8px 16px', borderRadius:'8px', cursor:'pointer', fontWeight:'700', fontSize:'12px', border:`1px solid ${active?scopeColors[scope]:'var(--border)'}`, background:active?`${scopeColors[scope]}15`:'var(--surface2)', color:active?scopeColors[scope]:'var(--text-muted)' }),
    saveBtn: { width:'100%', padding:'12px', background:'var(--signal)', color:'#000', fontWeight:'700', fontSize:'14px', borderRadius:'var(--radius)', border:'none', cursor:'pointer', marginTop:'8px' },
    cancelBtn: { width:'100%', padding:'12px', background:'var(--surface2)', color:'var(--text)', fontWeight:'600', fontSize:'14px', borderRadius:'var(--radius)', border:'1px solid var(--border)', cursor:'pointer', marginTop:'8px' },
    errorBox: { background:'var(--danger-bg)', border:'1px solid rgba(255,77,77,0.25)', color:'var(--danger)', borderRadius:'8px', padding:'10px 14px', fontSize:'13px', marginBottom:'14px' },
  };

  const endpoints = [
    { method:'GET', path:'/api/v1/status', desc:'Live status and current stream' },
    { method:'GET', path:'/api/v1/recordings', desc:'List recordings (paginated)' },
    { method:'GET', path:'/api/v1/destinations', desc:'Global destinations' },
    { method:'GET', path:'/api/v1/stream/health', desc:'Stream health metrics' },
    { method:'GET', path:'/api/v1/me', desc:'API key info' },
  ];

  return (
    <NavBar>
      <div style={s.page}>
      <nav style={s.nav}>
        <div style={s.navLogo}><div style={s.navDot}/>MultiStream</div>
        <div style={s.navRight}>
          <button style={s.navBtn(false)} onClick={()=>navigate('/dashboard')}>Dashboard</button>
          <button style={s.navBtn(false)} onClick={()=>navigate('/settings')}>Settings</button>
          {user?.role==='admin'&&<button style={s.navBtn(true)} onClick={()=>navigate('/admin')}>Users</button>}
          <button style={s.navBtn(false)} onClick={logout}>Sign out</button>
        </div>
      </nav>

      <div style={s.main}>
        <div style={s.header}>
          <div>
            <div style={s.title}>API Keys</div>
            <div style={{fontSize:'14px',color:'var(--text-muted)',marginTop:'4px'}}>Integrate external apps with Dennery FM</div>
          </div>
          <button style={s.addBtn} onClick={()=>setModal(true)}>+ New API Key</button>
        </div>

        {/* Revealed key - shown once */}
        {newKey && (
          <div style={s.keyBanner}>
            <div style={s.keyBannerTitle}>✓ API Key Created — "{newKey.name}"</div>
            <div style={s.keyBannerSub}>⚠️ Copy this key now. It will never be shown again.</div>
            <div style={s.keyDisplay}>{newKey.key}</div>
            <button style={s.copyBtn(copied)} onClick={copyKey}>
              {copied ? '✓ Copied!' : 'Copy Key'}
            </button>
            <button style={{...s.copyBtn(false), background:'transparent', color:'var(--text-muted)', border:'1px solid var(--border)', marginLeft:'8px'}} onClick={()=>setNewKey(null)}>
              Dismiss
            </button>
          </div>
        )}

        {/* Key list */}
        {keys.length === 0 && !newKey ? (
          <div style={{textAlign:'center',padding:'48px',color:'var(--text-muted)',border:'1px dashed var(--border)',borderRadius:'var(--radius-lg)',background:'var(--surface)'}}>
            <div style={{fontSize:'32px',marginBottom:'12px'}}>🔐</div>
            <div style={{fontWeight:'600',color:'var(--text)',marginBottom:'6px'}}>No API keys yet</div>
            Create an API key to connect your website, mobile app, or other services
          </div>
        ) : (
          keys.map(key => (
            <div key={key.id} style={s.keyCard(key.enabled)}>
              <div style={s.keyIcon}>🔑</div>
              <div style={s.keyInfo}>
                <div style={s.keyName}>{key.name}</div>
                <div style={s.keyMeta}>
                  <span style={s.mono}>{key.key_prefix}…</span>
                  {key.scopes.map(sc=><span key={sc} style={s.scopeBadge(sc)}>{sc}</span>)}
                  <span>{key.rate_limit_per_min}/min</span>
                  {key.last_used_at && <span>Used {new Date(key.last_used_at).toLocaleDateString()}</span>}
                  {key.expires_at && <span>Expires {new Date(key.expires_at).toLocaleDateString()}</span>}
                </div>
              </div>
              <div style={s.actions}>
                <button style={s.toggleBtn(key.enabled)} onClick={()=>toggleEnabled(key)}>
                  {key.enabled ? 'Enabled' : 'Disabled'}
                </button>
                <button style={s.delBtn} onClick={()=>deleteKey(key)}>Delete</button>
              </div>
            </div>
          ))
        )}

        {/* API Docs */}
        <div style={s.docsCard}>
          <div style={s.docsTitle}>API Reference</div>
          <div style={{fontSize:'13px',color:'var(--text-muted)',marginBottom:'12px'}}>
            Base URL: <span style={{fontFamily:'var(--mono)',color:'var(--signal)'}}>https://{window.location.host}/api</span>
            &nbsp;·&nbsp;
            Auth: <span style={{fontFamily:'var(--mono)'}}>Authorization: Bearer dfm_...</span>
          </div>
          {endpoints.map((e,i)=>(
            <div key={i} style={s.endpoint}>
              <span style={s.method(e.method)}>{e.method}</span>
              <span style={{color:'var(--signal)',flex:1}}>{e.path}</span>
              <span style={{color:'var(--text-muted)',fontSize:'11px'}}>{e.desc}</span>
            </div>
          ))}
        </div>
      </div>

      {modal && (
        <div style={s.overlay} onClick={e=>e.target===e.currentTarget&&setModal(false)}>
          <div style={s.modalBox}>
            <div style={s.modalTitle}>Create API Key</div>
            {error && <div style={s.errorBox}>{error}</div>}
            <div style={s.field}>
              <label style={s.label}>Name</label>
              <input placeholder="e.g. Website Integration" value={form.name} onChange={e=>setForm({...form,name:e.target.value})} />
            </div>
            <div style={s.field}>
              <label style={s.label}>Scopes</label>
              <div style={s.scopeGrid}>
                {['read','stream',...(user?.role==='admin'?['admin']:[])].map(scope=>(
                  <button key={scope} style={s.scopeBtn(form.scopes.includes(scope),scope)} onClick={()=>toggleScope(scope)}>
                    {scope}
                  </button>
                ))}
              </div>
              <div style={{fontSize:'12px',color:'var(--text-muted)',marginTop:'8px'}}>
                read — view status/recordings · stream — trigger actions · admin — full access
              </div>
            </div>
            <div style={s.field}>
              <label style={s.label}>Rate Limit (requests/min)</label>
              <input type="number" value={form.rate_limit_per_min} onChange={e=>setForm({...form,rate_limit_per_min:parseInt(e.target.value)})} style={{width:'120px'}} />
            </div>
            <div style={s.field}>
              <label style={s.label}>Expires (optional)</label>
              <input type="date" value={form.expires_at} onChange={e=>setForm({...form,expires_at:e.target.value})} />
            </div>
            <button style={s.saveBtn} onClick={handleCreate} disabled={loading}>
              {loading ? 'Creating…' : 'Create API Key'}
            </button>
            <button style={s.cancelBtn} onClick={()=>setModal(false)}>Cancel</button>
          </div>
        </div>
      )}
    </div>
    </NavBar>
  );
}
